import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartOptions
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { useTranslation } from "react-i18next";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import { StageAverageDurationChartDataType } from "@/components/layouts/StageAverageDuration/StageAverageDuration";
import { map, toLower } from "lodash";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  annotationPlugin,
  ChartDataLabels
);

export type drilldownChartDataType = {
  start: number;
  duration: number;
};

interface Props {
  chartData: drilldownChartDataType[];
  selectedElement?: StageAverageDurationChartDataType;
  maxValue: number;
}

const MINIMAL_MAX_WEEKS_TO_DISPLAY = 4;
const MINIMAL_BAR_WIDTH_VALUE = 0.05;

const DrilldownChart: React.FC<Props> = ({
  chartData,
  selectedElement,
  maxValue
}) => {
  const { t } = useTranslation();

  const getColors = (colorType: "background" | "border") => {
    return getColorForMilestoneByType(selectedElement?.milestone, colorType);
  };

  const data = {
    labels: map(chartData, (prop) => prop.duration),
    datasets: [
      {
        label: "Start",
        data: map(chartData, (prop) => prop.start),
        backgroundColor: "transparent"
      },
      {
        label: "Duration",
        data: map(chartData, (prop) =>
          Math.max(MINIMAL_BAR_WIDTH_VALUE, prop.duration)
        ),
        backgroundColor: getColors("background"),
        borderColor: getColors("border"),
        hoverBackgroundColor: getColors("background"),
        hoverBorderColor: getColors("border"),
        borderWidth: {
          top: 5,
          right: 0,
          bottom: 0,
          left: 0
        },
        borderRadius: 5,
        borderSkipped: false,
        barThickness: 30
      }
    ]
  };
  const options: ChartOptions<"bar"> = {
    maintainAspectRatio: false,
    responsive: true,
    indexAxis: "y",
    layout: {
      padding: {
        right: 150,
        top: -50
      }
    },
    scales: {
      x: {
        border: {
          color: "transparent"
        },
        grid: {
          display: false
        },
        position: "top",
        stacked: true,
        ticks: {
          display: false
        },
        max: Math.max(MINIMAL_MAX_WEEKS_TO_DISPLAY, maxValue)
      },
      y: {
        display: false,
        stacked: true
      }
    },
    plugins: {
      tooltip: {
        enabled: false
      },
      legend: {
        display: false
      },
      datalabels: {
        anchor: "end",
        align: "end",
        offset: 10,
        color: "#E4E7EC",
        font: {
          size: 14,
          family: "Open Sans"
        },
        formatter: function (value, context) {
          const datasetIndex = context.datasetIndex;
          if (datasetIndex === 1) {
            return `${Math.round(value)} ${toLower(t("stageAverageDuration.weeks"))}`;
          }
          return null;
        }
      }
    }
  };

  return (
    <div className="w-full h-full">
      <Bar data={data} options={options} />
    </div>
  );
};

export default DrilldownChart;
